import { Info } from "@tamagui/lucide-icons";
import React from "react";
import { allRoles } from "../../permissions/types";
import type {
  BusinessPlanModel,
  BusinessStaticUserInfoWithUserUid,
} from "../plan/BusinessPlanModel";
import type { BusinessFeedMention } from "./BusinessFeedItem";

export function buildFeedMentionsFromText(
  text: string,
  businessPlan: BusinessPlanModel,
): {
  text: string;
  mentions: BusinessFeedMention[];
  mentionUids: string[];
} {
  const knownEntities = businessPlan.mentionsLookup;
  const mentions: BusinessFeedMention[] = [];
  const foundUids = new Set<string>();

  // Sort names longest-first to avoid substring collisions (testlina before test)
  const sortedNames = Object.keys(knownEntities).sort(
    (a, b) => b.length - a.length,
  );

  for (const name of sortedNames) {
    const entity = knownEntities[name];

    // Build regex: must be preceded by start or whitespace, and followed by word boundary or punctuation
    // Support @name and #name, with Unicode letters
    const regex = new RegExp(
      `(^|[^\\p{L}\\p{N}_])([@#]${name})(?![\\p{L}\\p{N}_])`,
      "u",
    );

    if (!regex.test(text) || foundUids.has(entity.uid)) continue;

    const prefix = text.match(new RegExp(`[@#]${name}`, "u"))?.[0][0] ?? "@";

    mentions.push({
      uid: entity.uid,
      name: `${prefix}${name}`,
      type: entity.type,
    });

    foundUids.add(entity.uid);
  }

  return {
    text,
    mentions,
    mentionUids: mentions.map((m) => m.uid),
  };
}

export const getDisplayedRole = (user: BusinessStaticUserInfoWithUserUid) => {
  if (!!user.roles && user.roles.length) {
    // Find first role in prio order
    for (const r of allRoles.slice().reverse()) {
      if (user.roles.includes(r.name)) {
        return r;
      }
    }
  }

  return null;
};

export const useDisplayNameInfo = () => {
  return React.useCallback((user?: BusinessStaticUserInfoWithUserUid) => {
    if (!user) {
      return null;
    }

    if (user.title) {
      return {
        icon: Info,
        text: user.title,
      };
    }

    const role = getDisplayedRole(user);
    if (role)
      return {
        icon: role.emoji,
        text: role.displayName,
      };

    return null;
  }, []);
};
